/**
 * The contract list for ONE company — `useActiveBlanketOrders`, filtered by each order's
 * `Blanket Order.company` (see `lib/blanketOrderCompany.ts` for why the method cannot do it).
 *
 * While the companies are loading the list is held back rather than shown whole, so a contract
 * from another company is never offered and then withdrawn. If the lookup fails, everything is
 * shown — the same fail-open rule as the filter itself.
 */
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getResource } from './frappe'
import { str } from './wire'
import { useActiveBlanketOrders } from './useItems'
import { contractCompany, ordersForCompany } from '../blanketOrderCompany'
import { getSessionCompany } from './company'

export function useBlanketOrderCompanies() {
  return useQuery({
    queryKey: ['blanket-orders', 'companies'] as const,
    queryFn: async (): Promise<Map<string, string>> => {
      const page = await getResource<Record<string, unknown>>('Blanket Order', { fields: ['name', 'company'], limit: 1000 })
      return new Map(page.data.map((row) => [str(row.name), str(row.company)]))
    },
    staleTime: 30 * 60_000,
  })
}

export function useCompanyBlanketOrders(company: string | null | undefined) {
  const orders = useActiveBlanketOrders()
  const companies = useBlanketOrderCompanies()
  const settled = !companies.isPending
  // The enquiry's company, else the one chosen in settings — never the hard-coded fallback, which
  // would hide every other company's contracts from an enquiry that has no company yet.
  const scope = contractCompany(company, getSessionCompany())
  const data = useMemo(
    () => (orders.data && settled ? ordersForCompany(orders.data, companies.data ?? new Map(), scope) : undefined),
    [orders.data, settled, companies.data, scope],
  )
  return { ...orders, data, isLoading: orders.isLoading || !settled, isPending: orders.isPending || !settled }
}
